import { NSARouterMakerResult } from "namirasoft-account-react";
import { FilterItem, FilterItemOperator } from "namirasoft-core";
import { AccountRow, GifiRow, NamirasoftERPMetaDatabase } from "namirasoft-erp";
import { NSTitle } from "namirasoft-site-react";
import { Component, MouseEvent } from "react";
import { ErpRouterProps } from "../ErpRouterProps";
import { ErpRoutes } from "../ErpRoutes";
import { ErpScope } from "../ErpScope";

interface AccountHierarchyViewProps extends NSARouterMakerResult, ErpRouterProps
{
}

interface AccountHierarchyViewState
{
    roots: AccountRow[];
    childrenById: { [parentId: string]: AccountRow[] };
    loadingById: { [parentId: string]: boolean };
    loadedById: { [parentId: string]: boolean };
    expanded: { [id: string]: boolean };
    rootsLoading: boolean;
    error: string | null;
    gifiById: { [gifiId: string]: GifiRow };
    gifiLoadingById: { [gifiId: string]: boolean };
}

const TYPE_COLORS: { [k: string]: string } = {
    Asset: "#2e7d32",
    Liability: "#c62828",
    Equity: "#6a1b9a",
    Income: "#1565c0",
    Expense: "#ef6c00",
};

const TYPE_BG_COLORS: { [k: string]: string } = {
    Asset: "#e8f5e9",
    Liability: "#fdecea",
    Equity: "#f3e5f5",
    Income: "#e3f2fd",
    Expense: "#fff3e0",
};

export default class AccountHierarchyView extends Component<AccountHierarchyViewProps, AccountHierarchyViewState>
{
    override state: AccountHierarchyViewState = {
        roots: [],
        childrenById: {},
        loadingById: {},
        loadedById: {},
        expanded: {},
        rootsLoading: true,
        error: null,
        gifiById: {},
        gifiLoadingById: {},
    };

    private unsubscribeScope: (() => void) | null = null;

    override componentDidMount(): void
    {
        this.loadRoots();
        this.unsubscribeScope = ErpScope.subscribe(() => this.reload());
    }

    override componentWillUnmount(): void
    {
        if (this.unsubscribeScope)
            this.unsubscribeScope();
    }

    private reload(): void
    {
        this.setState({
            roots: [],
            childrenById: {},
            loadingById: {},
            loadedById: {},
            expanded: {},
            rootsLoading: true,
            error: null,
            gifiById: {},
            gifiLoadingById: {},
        }, () => this.loadRoots());
    }

    private loadGifisForRows(rows: AccountRow[]): void
    {
        let ids = new Set<string>();
        for (let r of rows)
        {
            if (r.gifi && !this.state.gifiById[r.gifi] && !this.state.gifiLoadingById[r.gifi])
                ids.add(r.gifi);
        }
        if (ids.size === 0) return;

        let marking: { [k: string]: boolean } = {};
        ids.forEach(id => { marking[id] = true; });
        this.setState(s => ({ gifiLoadingById: { ...s.gifiLoadingById, ...marking } }));

        ids.forEach(id =>
        {
            this.props.servers.erp.gifi.Get(id).then(row =>
            {
                this.setState(s =>
                {
                    let nextLoading = { ...s.gifiLoadingById };
                    delete nextLoading[id];
                    return {
                        gifiById: { ...s.gifiById, [id]: row },
                        gifiLoadingById: nextLoading,
                    };
                });
            }).catch(() =>
            {
                this.setState(s =>
                {
                    let nextLoading = { ...s.gifiLoadingById };
                    delete nextLoading[id];
                    return { gifiLoadingById: nextLoading };
                });
            });
        });
    }

    private sortRows(rows: AccountRow[]): AccountRow[]
    {
        return [...rows].sort((a, b) =>
        {
            let ca = (a.code ?? "").toString();
            let cb = (b.code ?? "").toString();
            if (ca && cb && ca !== cb)
                return ca.localeCompare(cb, undefined, { numeric: true });
            return (a.name ?? "").localeCompare(b.name ?? "");
        });
    }

    private async loadRoots(): Promise<void>
    {
        let companyId = ErpScope.getCompanyIdOrNull();
        let branchId = ErpScope.getBranchIdOrNull();
        if (!companyId || !branchId)
        {
            this.setState({ rootsLoading: false });
            return;
        }
        let meta = new NamirasoftERPMetaDatabase();
        try
        {
            let filters: FilterItem[] = [
                new FilterItem(meta.tables.account, meta.tables.account.columns.account_id, false, FilterItemOperator.all.empty),
                new FilterItem(meta.tables.account, meta.tables.account.columns.company_id, false, FilterItemOperator.all.equals, companyId),
                new FilterItem(meta.tables.account, meta.tables.account.columns.branch_id, false, FilterItemOperator.all.equals, branchId),
            ];
            let res = await this.props.servers.erp.account.List(filters, null, null, [], null);
            let sorted = this.sortRows(res.rows);
            this.setState({ roots: sorted, rootsLoading: false });
            this.loadGifisForRows(sorted);
        }
        catch (e: any)
        {
            this.setState({ rootsLoading: false, error: e?.message ?? "Failed to load accounts" });
        }
    }

    private async loadChildren(parentId: string): Promise<void>
    {
        if (this.state.loadedById[parentId] || this.state.loadingById[parentId])
            return;

        let companyId = ErpScope.getCompanyIdOrNull();
        let branchId = ErpScope.getBranchIdOrNull();
        if (!companyId || !branchId)
            return;

        let meta = new NamirasoftERPMetaDatabase();

        this.setState(s => ({ loadingById: { ...s.loadingById, [parentId]: true } }));
        try
        {
            let filters: FilterItem[] = [
                new FilterItem(meta.tables.account, meta.tables.account.columns.account_id, false, FilterItemOperator.all.equals, parentId),
                new FilterItem(meta.tables.account, meta.tables.account.columns.company_id, false, FilterItemOperator.all.equals, companyId),
                new FilterItem(meta.tables.account, meta.tables.account.columns.branch_id, false, FilterItemOperator.all.equals, branchId),
            ];
            let res = await this.props.servers.erp.account.List(filters, null, null, [], null);
            let sorted = this.sortRows(res.rows);
            this.setState(s => ({
                childrenById: { ...s.childrenById, [parentId]: sorted },
                loadingById: { ...s.loadingById, [parentId]: false },
                loadedById: { ...s.loadedById, [parentId]: true },
            }));
            this.loadGifisForRows(sorted);
        }
        catch (e: any)
        {
            this.setState(s => ({
                loadingById: { ...s.loadingById, [parentId]: false },
                error: e?.message ?? "Failed to load child accounts",
            }));
        }
    }

    private toggle(row: AccountRow): void
    {
        if (!row.is_group) return;
        let id = row.id;
        let willExpand = !this.state.expanded[id];
        this.setState(s => ({ expanded: { ...s.expanded, [id]: willExpand } }));
        if (willExpand && !this.state.loadedById[id])
            this.loadChildren(id);
    }

    private openInNewTab(path: string): void
    {
        window.open(path, "_blank", "noopener,noreferrer");
    }

    private openView(id: string): void
    {
        this.openInNewTab(ErpRoutes.AccountView(id));
    }

    private openEdit(id: string): void
    {
        this.openInNewTab(ErpRoutes.AccountEdit(id));
    }

    private renderActionButton(label: string, color: string, onClick: (e: MouseEvent) => void): JSX.Element
    {
        return (
            <button
                onClick={(e) =>
                {
                    e.stopPropagation();
                    onClick(e);
                }}
                style={{
                    background: "#fff",
                    color,
                    border: `1px solid ${color}`,
                    borderRadius: 4,
                    padding: "4px 12px",
                    fontSize: 12,
                    cursor: "pointer",
                    marginLeft: 6,
                }}
            >
                {label}
            </button>
        );
    }

    private renderRow(row: AccountRow, depth: number): JSX.Element
    {
        let isExpanded = !!this.state.expanded[row.id];
        let isLoading = !!this.state.loadingById[row.id];
        let isLoaded = !!this.state.loadedById[row.id];
        let typeColor = TYPE_COLORS[row.type as any] ?? "#555";
        let children = this.state.childrenById[row.id] ?? [];
        let canExpand = row.is_group;

        let chevron: string;
        if (!canExpand) chevron = "•";
        else if (isLoading) chevron = "…";
        else chevron = isExpanded ? "▾" : "▸";

        return (
            <div key={row.id}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "16px 12px",
                        paddingLeft: 12 + depth * 24,
                        minHeight: 64,
                        borderBottom: "1px solid #f0f0f0",
                        cursor: canExpand ? "pointer" : "default",
                        background: row.is_group ? "#fafbff" : (TYPE_BG_COLORS[row.type as any] ?? "#fff"),
                    }}
                    onClick={() => this.toggle(row)}
                >
                    <span
                        style={{
                            fontSize: 11,
                            color: typeColor,
                            border: `1px solid ${typeColor}`,
                            borderRadius: 4,
                            padding: "2px 8px",
                            marginRight: 12,
                            minWidth: 80,
                            textAlign: "center",
                            display: "inline-block",
                        }}
                    >
                        {row.type}
                    </span>
                    <span
                        style={{
                            width: 22,
                            display: "inline-block",
                            textAlign: "center",
                            color: "#666",
                            fontSize: 16,
                            userSelect: "none",
                        }}
                    >
                        {chevron}
                    </span>
                    <span style={{ fontFamily: "monospace", color: "#444", marginRight: 14, minWidth: 100 }}>
                        {row.code ?? ""}
                    </span>
                    <span style={{ fontWeight: row.is_group ? 600 : 400, color: "#222", flex: 1, fontSize: 14 }}>
                        {row.name}
                    </span>
                    {!row.enabled && (
                        <span style={{ fontSize: 11, color: "#999", marginLeft: 8, fontStyle: "italic" }}>
                            disabled
                        </span>
                    )}
                    {row.gifi && (
                        <span
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                marginLeft: 8,
                                padding: "2px 8px",
                                fontSize: 11,
                                color: "#37474f",
                                background: "#eceff1",
                                border: "1px solid #cfd8dc",
                                borderRadius: 4,
                            }}
                            title={this.state.gifiById[row.gifi]?.name ?? ""}
                        >
                            <span style={{ fontFamily: "monospace", fontWeight: 600 }}>GIFI {row.gifi}</span>
                            <span style={{ color: "#546e7a" }}>
                                {this.state.gifiById[row.gifi]?.name
                                    ?? (this.state.gifiLoadingById[row.gifi] ? "…" : "")}
                            </span>
                        </span>
                    )}
                    {this.renderActionButton("View", "#1565c0", () => this.openView(row.id))}
                    {this.renderActionButton("Edit", "#ef6c00", () => this.openEdit(row.id))}
                </div>
                {canExpand && isExpanded && (
                    <div>
                        {isLoading && (
                            <div style={{ padding: "12px", paddingLeft: 12 + (depth + 1) * 24, color: "#888", fontStyle: "italic" }}>
                                Loading...
                            </div>
                        )}
                        {!isLoading && isLoaded && children.length === 0 && (
                            <div style={{ padding: "12px", paddingLeft: 12 + (depth + 1) * 24, color: "#999", fontStyle: "italic" }}>
                                No child accounts.
                            </div>
                        )}
                        {!isLoading && children.map(c => this.renderRow(c, depth + 1))}
                    </div>
                )}
            </div>
        );
    }

    override render()
    {
        let { rootsLoading, error, roots } = this.state;
        let companyId = ErpScope.getCompanyIdOrNull();
        let branchId = ErpScope.getBranchIdOrNull();

        let body: JSX.Element;
        if (!companyId || !branchId)
            body = <div style={{ padding: 16, color: "#666" }}>Please select a company and branch from the scope bar.</div>;
        else if (rootsLoading)
            body = <div style={{ padding: 16, color: "#666" }}>Loading...</div>;
        else if (error)
            body = <div style={{ padding: 16, color: "#c62828" }}>{error}</div>;
        else if (roots.length === 0)
            body = <div style={{ padding: 16, color: "#666" }}>No accounts found.</div>;
        else
            body = <div>{roots.map(r => this.renderRow(r, 0))}</div>;

        return (
            <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 8, alignItems: "stretch" }}>
                <NSTitle title="Account Hierarchy" />
                <div
                    style={{
                        width: "100%",
                        background: "#fff",
                        border: "1px solid rgba(161, 164, 176, 1)",
                        borderRadius: 8,
                        boxShadow: "0px 0px 2px 0px rgba(161, 164, 176, 1)",
                        overflow: "hidden",
                    }}
                >
                    {body}
                </div>
            </div>
        );
    }
}